import React, { ButtonHTMLAttributes } from 'react'
import { isEmpty } from 'lodash'
import clsx from 'clsx'

import Loader from '../Loader'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Is this the principal call to action on the page?
   */
  variants?: 'primary' | 'secondary' | 'ternary' | 'outline'
  /**
   * Is this the principal call to action on the page?
   */
  as?: 'link'
  /**
   * Optional click handler
   */
  onClick?: () => void
  /**
   * Optional children handler
   */
  children: React.ReactNode
  /**
   * Optional as tag
   */
  href?: string
  /**
   * Optional as Icon
   */
  withIcon?: React.ReactNode
  /**
   * Optional as status
   */
  isLoading?: boolean
}

/**
 * Primary UI component for user interaction
 */
export const Button: React.FC<ButtonProps> = (
  props: ButtonProps,
): JSX.Element => {
  const {
    type = 'button',
    as,
    variants = 'primary',
    disabled = false,
    href,
    children,
    className,
    isLoading = false,
    withIcon,
    ...others
  } = props

  const modifierIcon = isLoading ? <Loader variants='ternary' /> : withIcon
  const base = href || as === 'link' ? 'button__a' : 'button'
  const hasIcon = typeof modifierIcon === 'object' && !isEmpty(modifierIcon)
  const hasButton = !isEmpty(children)
  const hasOnlyIcon = hasIcon && !hasButton
  const hasButtonWithIcon = hasButton && hasIcon

  const variantsCn =
    variants && disabled
      ? clsx(`${base}__disabled`, `${base}__disabled--${variants}`)
      : `${base}--${variants}`

  const cn = clsx(
    base,
    variantsCn,
    { 'button--without-min-width': hasOnlyIcon },
    className,
  )

  const buttonProps = {
    ...others,
    type,
    className: cn,
  }

  if (href) {
    return (
      <a href={href} className={cn}>
        {children}
      </a>
    )
  }

  if (as === 'link') {
    return (
      <a className={cn} {...(others.onClick && { onClick: others.onClick })}>
        {children}
      </a>
    )
  }

  return (
    <button {...buttonProps} {...others} disabled={disabled || isLoading}>
      {hasIcon && (
        <span
          className={clsx('button__icon', {
            ['button__icon--loading']: isLoading,
            [`button__icon--has-text`]: hasButtonWithIcon && !isLoading,
          })}
        >
          {modifierIcon}
        </span>
      )}
      {!isLoading && children}
    </button>
  )
}

export default Button
